import { createPositionControl } from '@/components/CustomLocationControl'
import { createMarkerCluster } from '@/components/MapCluster'
import useBrandImageResolver from '@/components/useBrandImageResolver'

import config from '@/config'
import language from '@/lang/index.json'
import { createMarkers } from '@/lib/createMarkers'
import filterMapMarkersByAssortments from '@/lib/filterMapMarkerByAssortments'
import useDebouncedVisibleMarkers from '@/lib/useDebouncedVisibleMarkers'
import useCenter from '@/lib/useMapCenter'
import { useMobile } from '@/lib/useMedia'
import type { Language, Place, Store } from '@/types'

const isMobile = useMobile()
const { center, updateCenter } = useCenter()
const brand = import.meta.env.PUBLIC_APP_BRAND

interface GoogleMapProps {
  response: { features: Store[] } | undefined
  lang: Language
  navigateTo: (place: Place) => void
}

const GoogleMap = async ({ response, lang, navigateTo }: GoogleMapProps) => {
  const { Map } = (await google.maps.importLibrary(
    'maps',
  )) as google.maps.MapsLibrary

  const settings = config.settings[lang][isMobile.value ? 'mobile' : 'desktop']

  updateCenter(settings.center)

  const map = new Map(document.getElementById('map') as HTMLElement, {
    center: center.value,
    zoom: settings.defaultZoom,
    maxZoom: 18,
    minZoom: settings.defaultZoom,
    fullscreenControl: false,
    streetViewControl: false,
    mapTypeControl: false,
  })

  const infoWindow = new google.maps.InfoWindow({
    content: '',
    disableAutoPan: true,
  })

  const url = useBrandImageResolver(brand, 'marker.webp')

  const markers = createMarkers({
    url,
    infoWindow,
    map,
    stores: response,
    navigateTo,
  })

  const markerCluster = createMarkerCluster({ map, markers: [] })

  map.addListener('bounds_changed', async () => {
    const newMarkers = await useDebouncedVisibleMarkers({
      map,
      markers,
      response,
    })

    if (newMarkers) markerCluster.addMarkers(newMarkers)
  })

  document.addEventListener('assortmentChanged', (event) => {
    filterMapMarkersByAssortments({
      markers,
      map,
      response,
      event,
    })
  })

  const positionControl = createPositionControl(
    map,
    language[lang].locationDisabled,
    lang,
  )

  map.controls[google.maps.ControlPosition.RIGHT_BOTTOM].push(positionControl)

  return map
}

export default GoogleMap
